<template>
  <div v-if="summary" class="flex items-center gap-1 text-xs text-gray-500">
    <Icon :name="summary.icon" class="size-3" :class="iconClass" />
    <span>{{ summary.label }}</span>
    <span v-if="duration !== undefined" class="text-gray-400"
      >({{ duration }}ms)</span
    >
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from "vue";
import Icon from "@/components/Core/Icon.vue";
import type { IconName } from "@/services/icons";

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  status: {
    type: String as PropType<"running" | "complete" | undefined>,
    default: undefined,
  },
  duration: {
    type: Number as PropType<number | undefined>,
    default: undefined,
  },
});

const TOOL_LABELS: Record<
  string,
  { label: string; icon: IconName } | undefined
> = {
  list_entities: { label: "Looking up entities", icon: "list-ul" },
  get_metrics: { label: "Analyzing performance", icon: "ad-solid" },
  get_search_terms: {
    label: "Analyzing search terms",
    icon: "search-dollar",
  },
  get_website_metrics: { label: "Analyzing website traffic", icon: "browser" },
  get_search_console: {
    label: "Fetching search console data",
    icon: "google-search-console",
  },
  get_social_page_metrics: {
    label: "Analyzing social media",
    icon: "comment-dots",
  },
  get_social_posts: {
    label: "Analyzing social posts",
    icon: "comment-dots",
  },
  get_ecommerce_metrics: {
    label: "Fetching e-commerce data",
    icon: "shopping-cart",
  },
  get_store_metrics: { label: "Fetching store data", icon: "shopping-cart" },
  get_audience: { label: "Analyzing audience", icon: "chart-box" },
  get_heatmap: { label: "Computing heatmap", icon: "chart-box" },
  get_share_of_search: {
    label: "Computing share of search",
    icon: "search-dollar",
  },
  get_competitor_followers: {
    label: "Fetching competitor followers",
    icon: "comment-dots",
  },
  get_competitor_metrics_comparison: {
    label: "Comparing against competitors",
    icon: "chart-box",
  },
  create_dashboard: { label: "Creating dashboard", icon: "chart-box" },
  update_dashboard: { label: "Updating dashboard", icon: "chart-box" },
  create_widget_simple: { label: "Adding widget", icon: "chart-box" },
  create_goal_or_kpi_widget: {
    label: "Adding goal/KPI widget",
    icon: "chart-box",
  },
  manage_tab: { label: "Updating tabs", icon: "chart-box" },
  manage_widgets: { label: "Updating widgets", icon: "chart-box" },
  manage_competitor: { label: "Managing competitor", icon: "comment-dots" },
  manage_goal: { label: "Managing goal", icon: "chart-box" },
  manage_kpi: { label: "Managing KPI", icon: "chart-box" },
  manage_annotation: { label: "Managing annotation", icon: "chart-box" },
  get_reference: {
    label: "Reading reference docs",
    icon: "chart-box",
  },
  get_available_metrics: {
    label: "Checking available metrics",
    icon: "chart-box",
  },
  get_conversion_metrics: {
    label: "Analyzing conversions",
    icon: "chart-box",
  },
};

const summary = computed<{ label: string; icon: IconName }>(() => {
  const mapped = TOOL_LABELS[props.name];
  if (mapped) {
    return {
      label:
        props.status === "complete"
          ? mapped.label.replace(/ing\b/, "ed").replace("Fetch", "Fetch")
          : mapped.label,
      icon: props.status === "running" ? "spinner-third-duotone" : mapped.icon,
    };
  }
  return {
    label: props.name.replace(/_/g, " "),
    icon: props.status === "running" ? "spinner-third-duotone" : "wand-magic",
  };
});

const iconClass = computed(() =>
  props.status === "running" ? "animate-spin text-blue-600" : "",
);
</script>
